import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useFetcher } from '@remix-run/react';
import {
    MultiStepFormContainer,
    useMultiStepForm,
} from '~/ui-rocco/multi-step-form';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import { useUserLoggedIn } from '@modules/user';
import { useToaster } from '~/modules/root/context/ToasterContext';
import type { StepConfig } from '~/ui-rocco/multi-step-form';
import type { User } from '~/server/types';
import { ReactComponent as ArrowLeft } from '@rocco/icons/svg/arrow/arrow-left.svg';
import type { HdeProfessionalExtraction } from '../utils/extract-professional-for-hde';
import { useHdeProfessionalExtraction } from './use-hde-professional-extraction';
import { useHdeExistingSubmission } from './use-hde-existing-submission';
import type { HDEExistingSubmission } from '~/routes/remix-api.app.hde-existing-submission';
import { HDE_EVENT_GUEST_BLOCK_MESSAGE, HDE_PAGE_PATH } from '../constants';
import { resolveHdeStepIndexAfterVisibleStepsChange } from '../utils/realign-hde-step-index';
import { shouldBlockEventGuest } from '../utils/should-block-event-guest';

const HIDE_INDICATOR_STEP_IDS = new Set([
    'intro',
    'application-type',
    'authentication',
    'success',
    'existing-submission',
]);

export interface FilterVisibleStepsContext {
    isLoggedIn: boolean;
    user: User | null;
    locationOptionsLength: number;
    formData?: unknown;
    socialLoginSubmitted?: boolean;
    /** Number of business profiles for logged-in Me (professional/exhibitor flows). */
    profileCount?: number;
    /** Extracted CompanyType and ProjectLocation from selected Professional profile. */
    professionalExtraction?: HdeProfessionalExtraction | null;
    /** When true, keep company-type and company-location visible (validation error recovery). */
    hasCompanyValidationError?: boolean;
    /** True when the member already has a submission for this event (shows the update gate). */
    hasExistingSubmission?: boolean;
}

export interface GetInitialDataContext {
    locationOptions: unknown;
    user: User | null;
    isLoggedIn: boolean;
    showForgotPassword: boolean;
    showEmailForm: boolean;
    handleAuthEmailFormBack: () => void;
    onClose?: () => void;
    stageConfig?: unknown;
    isSubmitSuccess: boolean;
    socialLoginSubmitted: boolean;
    /** Extracted CompanyType and ProjectLocation from selected Professional profile. */
    professionalExtraction?: HdeProfessionalExtraction | null;
}

interface UseHDERegistrationFormParams<TFormData> {
    initialFormData: TFormData;
    stepConfigs: StepConfig[];
    /** When 'split', renders two-panel layout with hero left and form right */
    layout?: 'single' | 'split';
    filterVisibleSteps: (
        configs: StepConfig[],
        context: FilterVisibleStepsContext,
    ) => StepConfig[];
    getInitialDataForStep: (
        stepId: string,
        formData: TFormData,
        context: GetInitialDataContext,
    ) => Record<string, unknown> | undefined;
    buildSubmitPayload: (
        formData: TFormData,
        user: User | null,
        professionalExtraction?: HdeProfessionalExtraction | null,
    ) => FormData;
    /** When true, handleNext and handleBack/handleCancelOrBack include guest-invitation logic */
    hasGuestInvitation?: boolean;
    locationOptions: unknown;
    /** Optional stage config for HDE homeowner form */
    stageConfig?: unknown;
    onClose?: () => void;
    onStepChange?: (stepId: string) => void;
    onAuthViewChange?: (showEmailForm: boolean) => void;
    onAuthLoadingChange?: (loading: boolean) => void;
    socialLoginSubmitted?: boolean;
    /** When on first step and Back is clicked, call this to return to application type selection */
    onBackToApplicationType?: () => void;
    /** When on application-type step, use this as total for step indicator (default: Homeowner count) */
    defaultTotalStepsForIndicator?: number;
    /** Initial step index to start the form at (defaults to 0) */
    initialStepIndex?: number;
    /** Offset applied to visual dot index (e.g. 1 so sub-forms start at step 2) */
    indicatorStepOffset?: number;
    /** Returns URLSegment of selected business profile for extraction fetch. Used when extraction is needed. */
    getSelectedProfessionalUrlSegment?: (
        formData: TFormData,
        user: User | null,
    ) => string | null;
    /** Application type for step indicator overrides on professional-member step. */
    applicationType?: 'Professional' | 'Exhibitor' | 'Homeowner';
    /** Resolves the numeric EventID being registered for, to look up an existing submission. */
    getSelectedEventId?: (formData: TFormData) => number | null;
    /** Maps a previously-submitted record onto this form's data shape for prefilling. */
    mapExistingSubmissionToFormData?: (
        existing: HDEExistingSubmission,
    ) => Partial<TFormData>;
    /** Switch the modal's application type (used by the existing-submission gate to
     *  "continue as" the member's existing registration type). */
    onSwitchApplicationType?: (
        type: 'Homeowner' | 'Professional' | 'Exhibitor',
    ) => void;
    /** When true, the existing-submission gate is hidden (already resolved by the member). */
    suppressExistingSubmissionGate?: boolean;
    /** Called when the member resolves the duplicate gate (update or switch type). */
    onExistingSubmissionGateResolved?: () => void;
}

interface UseHDERegistrationFormReturn<TFormData> {
    formData: TFormData;
    setFormData: React.Dispatch<React.SetStateAction<TFormData>>;
    visibleSteps: StepConfig[];
    safeStepIndex: number;
    currentStepId: string | undefined;
    isSubmitting: boolean;
    uiFormState: ReturnType<typeof useMultiStepForm>;
    handleStepDataChange: (data: unknown) => void;
    handleNext: () => void;
    handleBack: () => void;
    handleCancelOrBack: () => void;
    getInitialDataForStep: (
        stepId: string,
    ) => Record<string, unknown> | undefined;
    isAuthInitialView: boolean;
    hideStepIndicator: boolean;
    hideFooter: boolean;
    indicatorSteps: StepConfig[];
    currentVisualDotIndex: number;
    renderForm: () => React.ReactElement;
}

/**
 * Shared hook for HDE registration forms (Exhibitor, Professional, Homeowner).
 * Consolidates state, submission, response processing, timeout management,
 * and step indicator logic. Parameterised by step configs, form data, and
 * submission builder.
 */
export function useHDERegistrationForm<TFormData extends object>(
    params: UseHDERegistrationFormParams<TFormData>,
): UseHDERegistrationFormReturn<TFormData> {
    const {
        initialFormData,
        stepConfigs,
        layout = 'single',
        filterVisibleSteps,
        getInitialDataForStep: getInitialDataForStepParam,
        buildSubmitPayload,
        hasGuestInvitation = false,
        locationOptions,
        stageConfig,
        onClose,
        onStepChange,
        onAuthViewChange,
        onAuthLoadingChange,
        socialLoginSubmitted = false,
        onBackToApplicationType,
        defaultTotalStepsForIndicator,
        initialStepIndex = 0,
        indicatorStepOffset = 0,
        getSelectedProfessionalUrlSegment,
        applicationType,
        getSelectedEventId,
        mapExistingSubmissionToFormData,
        onSwitchApplicationType,
        suppressExistingSubmissionGate,
        onExistingSubmissionGateResolved,
    } = params;

    const { user, isLoggedIn } = useUserLoggedIn();
    const { mobile } = useAppDisplayModeContext();
    const fetcher = useFetcher();
    const [formData, setFormData] = useState<TFormData>(initialFormData);
    const { toast } = useToaster();

    const selectedUrlSegment =
        getSelectedProfessionalUrlSegment?.(formData, user) ?? null;
    const {
        extraction: professionalExtraction,
        isLoading: isProfessionalExtractionLoading,
    } = useHdeProfessionalExtraction(selectedUrlSegment);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
    const [hasCompanyValidationError, setHasCompanyValidationError] =
        useState(false);
    const wasOnAuthStep = useRef(false);
    const prevIsLoggedIn = useRef(isLoggedIn);
    const submissionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const hasProcessedResponseRef = useRef(false);

    const profileCount = useMemo(() => {
        const u = user as {
            __typename?: string;
            Professionals?: unknown[];
        } | null;
        if (u?.__typename === 'Me' && Array.isArray(u.Professionals)) {
            return u.Professionals.length;
        }
        return 0;
    }, [user]);

    // ─── Existing-submission detection (duplicate registration) ──────
    const memberKey =
        user && (user as { __typename?: string }).__typename === 'Me'
            ? String((user as { ID: number }).ID)
            : null;
    // Fires as soon as the member is authenticated and the event is known (after the
    // application-type selection), so the update gate can be the first sub-form step.
    const selectedEventId = getSelectedEventId?.(formData) ?? null;
    const {
        existingSubmission,
        isEventGuest,
        isChecking: isCheckingExistingSubmission,
    } = useHdeExistingSubmission(
        selectedEventId,
        isLoggedIn && Boolean(selectedEventId),
        memberKey,
    );
    const existingSubmissionSameType =
        existingSubmission != null &&
        existingSubmission.applicationType === applicationType;
    // The gate is hidden once the member has resolved it (e.g. chose "continue as my
    // existing type", which switched the sub-form and should not re-prompt).
    // Also hide during/after an in-flight submit so a late existing-submission lookup
    // cannot prepend a step and shift the success screen back to guest-invitation.
    const showExistingSubmissionGate =
        existingSubmission != null &&
        !suppressExistingSubmissionGate &&
        !hasSubmitted &&
        !isSubmitSuccess;
    // An invited guest (live ticket, no primary submission) cannot register as primary.
    const showEventGuestBlock = shouldBlockEventGuest({
        isEventGuest,
        suppressExistingSubmissionGate: Boolean(suppressExistingSubmissionGate),
        hasSubmitted,
        isSubmitSuccess,
    });
    // Fire the explanatory toast once per resolution, when the block first becomes active.
    const eventGuestToastShownRef = useRef(false);
    useEffect(() => {
        if (showEventGuestBlock && !eventGuestToastShownRef.current) {
            eventGuestToastShownRef.current = true;
            toast(HDE_EVENT_GUEST_BLOCK_MESSAGE, { type: 'error' });
        }
        if (!showEventGuestBlock) {
            eventGuestToastShownRef.current = false;
        }
    }, [showEventGuestBlock, toast]);
    // Tracked in state (not a ref) so the checking view can keep the spinner up until
    // the prefill has actually been committed to formData. Otherwise step 0 mounts
    // with empty formData and its internal useState(initialData?.x) captures null —
    // so option-list buttons never light up as selected.
    const [prefillAppliedFor, setPrefillAppliedFor] = useState<string | null>(
        null,
    );
    const canPrefill = Boolean(mapExistingSubmissionToFormData);
    const needsPrefill =
        canPrefill &&
        existingSubmission != null &&
        existingSubmission.applicationType === applicationType &&
        prefillAppliedFor !== existingSubmission.id;

    useEffect(() => {
        if (!existingSubmission) return;
        if (prefillAppliedFor === existingSubmission.id) return;

        // Skip the merge when the chosen type differs from the existing submission
        // (switching to a different type starts from a blank form), or when no mapper
        // is provided — but still record the resolution so the gating spinner exits.
        const sameType = existingSubmission.applicationType === applicationType;
        if (sameType && mapExistingSubmissionToFormData) {
            const mapped = mapExistingSubmissionToFormData(existingSubmission);
            // The spinner blocks step 0 until this effect runs, so the user cannot
            // have typed anything yet — safe to overwrite the initial defaults
            // (e.g. `guestFormView: false → true` so the prefilled guest form opens).
            // Drop null / undefined / empty-string entries so we never replace a
            // sensible default with nothing.
            const cleaned = Object.fromEntries(
                Object.entries(mapped).filter(([, v]) => v != null && v !== ''),
            );
            setFormData((prev) => ({ ...prev, ...cleaned }) as TFormData);
        }
        setPrefillAppliedFor(existingSubmission.id);
    }, [
        existingSubmission,
        applicationType,
        mapExistingSubmissionToFormData,
        prefillAppliedFor,
    ]);

    const visibleSteps = useMemo(
        () =>
            filterVisibleSteps(stepConfigs, {
                isLoggedIn,
                user:
                    user &&
                    (user as { __typename?: string }).__typename === 'Me'
                        ? user
                        : null,
                locationOptionsLength: Array.isArray(locationOptions)
                    ? locationOptions.length
                    : 0,
                formData,
                socialLoginSubmitted,
                profileCount,
                professionalExtraction,
                hasCompanyValidationError,
                hasExistingSubmission: showExistingSubmissionGate,
            }),
        [
            stepConfigs,
            filterVisibleSteps,
            isLoggedIn,
            locationOptions,
            formData,
            socialLoginSubmitted,
            profileCount,
            user,
            professionalExtraction,
            hasCompanyValidationError,
            showExistingSubmissionGate,
        ],
    );

    const uiFormState = useMultiStepForm(visibleSteps.length, initialStepIndex);
    const goToNextRef = useRef(uiFormState.goToNext);
    goToNextRef.current = uiFormState.goToNext;
    const goToPreviousRef = useRef(uiFormState.goToPrevious);
    goToPreviousRef.current = uiFormState.goToPrevious;
    const goToStepRef = useRef(uiFormState.goToStep);
    goToStepRef.current = uiFormState.goToStep;
    const visibleStepIdsRef = useRef<string[]>([]);

    const safeStepIndex = Math.min(
        uiFormState.currentStepIndex,
        Math.max(0, visibleSteps.length - 1),
    );
    const currentStep = visibleSteps[safeStepIndex];
    const currentStepId = currentStep?.id;

    useEffect(() => {
        if (uiFormState.currentStepIndex !== safeStepIndex) {
            uiFormState.goToStep(safeStepIndex);
        }
    }, [safeStepIndex, uiFormState]);

    useEffect(() => {
        const previousStepIds = visibleStepIdsRef.current;
        const currentStepIds = visibleSteps.map((step) => step.id);
        const nextStepIndex = resolveHdeStepIndexAfterVisibleStepsChange(
            previousStepIds,
            currentStepIds,
            uiFormState.currentStepIndex,
            {
                showExistingSubmissionGate,
                hasSubmitted,
                isSubmitSuccess,
            },
        );

        if (
            nextStepIndex != null &&
            nextStepIndex !== uiFormState.currentStepIndex
        ) {
            uiFormState.goToStep(nextStepIndex);
        }

        visibleStepIdsRef.current = currentStepIds;
    }, [
        visibleSteps,
        uiFormState,
        showExistingSubmissionGate,
        hasSubmitted,
        isSubmitSuccess,
    ]);

    const [showEmailForm, setShowEmailForm] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    /** Disables modal footer Continue while login/sign-up fetchers run (HDE auth step). */
    const [authEmailPasswordFetchersBusy, setAuthEmailPasswordFetchersBusy] =
        useState(false);
    /** Pro/exhibitor company step: synced from step UI; blocks Next with a toast if false. */
    const [
        hdeCompanyMarketingConsentChecked,
        setHdeCompanyMarketingConsentChecked,
    ] = useState(false);

    const handleAuthEmailFormBack = useCallback(() => {
        if (showForgotPassword) {
            setShowForgotPassword(false);
        } else {
            uiFormState.goToPrevious();
        }
    }, [showForgotPassword, uiFormState]);

    const hasSuccessfulSubmission = isSubmitSuccess || socialLoginSubmitted;

    const getInitialDataContext: GetInitialDataContext = useMemo(
        () => ({
            locationOptions,
            user:
                user && (user as { __typename?: string }).__typename === 'Me'
                    ? user
                    : null,
            isLoggedIn,
            showForgotPassword,
            showEmailForm,
            handleAuthEmailFormBack,
            onClose,
            stageConfig,
            // Social login can complete submission on the server before the user
            // returns from OAuth, so the success step should render immediately.
            isSubmitSuccess: hasSuccessfulSubmission,
            socialLoginSubmitted,
            professionalExtraction,
        }),
        [
            locationOptions,
            user,
            isLoggedIn,
            showForgotPassword,
            showEmailForm,
            handleAuthEmailFormBack,
            onClose,
            stageConfig,
            hasSuccessfulSubmission,
            socialLoginSubmitted,
            professionalExtraction,
        ],
    );

    const getInitialDataForStep = useCallback(
        (stepId: string) => {
            const initialData = getInitialDataForStepParam(
                stepId,
                formData,
                getInitialDataContext,
            );

            if (stepId === 'guest-invitation') {
                return {
                    ...initialData,
                    eventId: selectedEventId,
                    existingSubmissionId: existingSubmission?.id ?? null,
                };
            }

            if (stepId === 'existing-submission' && existingSubmission) {
                const existingType = existingSubmission.applicationType;
                return {
                    ...initialData,
                    firstName: existingSubmission.firstName,
                    existingType,
                    selectedType: applicationType,
                    sameType: existingSubmissionSameType,
                    // Keep the existing registration. When it is a different type,
                    // switch the modal to that type (the target form prefills and skips
                    // this gate); same type just continues with the prefilled answers.
                    onContinueExisting: () => {
                        if (
                            existingType &&
                            existingType !== applicationType &&
                            onSwitchApplicationType
                        ) {
                            onSwitchApplicationType(existingType);
                            return;
                        }
                        // Drop the gate first so index 0 becomes the prefilled form.
                        onExistingSubmissionGateResolved?.();
                    },
                    // Start a fresh registration as the selected type, discarding the
                    // previous answers (the backend replaces the record on submit).
                    onSwitchToSelected: () => {
                        const switchedFormData = {
                            ...initialFormData,
                        } as TFormData;
                        // Logged-in members with profiles should pick a business first,
                        // not land on manual company details after the gate closes.
                        if (
                            isLoggedIn &&
                            profileCount >= 1 &&
                            'isProfessionalMember' in initialFormData
                        ) {
                            (
                                switchedFormData as {
                                    isProfessionalMember: boolean | null;
                                }
                            ).isProfessionalMember = true;
                        }
                        setFormData(switchedFormData);
                        onExistingSubmissionGateResolved?.();
                    },
                    onCancel: () => onClose?.(),
                };
            }

            if (stepId !== 'professional-member') {
                return initialData;
            }

            return {
                ...initialData,
                onProfessionalMemberSelected: (
                    isProfessionalMember: boolean,
                ) => {
                    if (isLoggedIn && isProfessionalMember) {
                        uiFormState.goToStep(uiFormState.currentStepIndex);
                        return;
                    }
                    uiFormState.goToNext();
                },
            };
        },
        [
            formData,
            getInitialDataForStepParam,
            getInitialDataContext,
            isLoggedIn,
            uiFormState,
            existingSubmission,
            existingSubmissionSameType,
            applicationType,
            onSwitchApplicationType,
            onExistingSubmissionGateResolved,
            onClose,
            initialFormData,
            selectedEventId,
            profileCount,
        ],
    );

    const handleStepDataChange = useCallback(
        (data: unknown) => {
            const stepData = data as Partial<TFormData> & {
                showEmailForm?: boolean;
                showForgotPassword?: boolean;
                _authLoading?: boolean;
                _authFetchersBusy?: boolean;
                _hdeCompanyMarketingConsent?: boolean;
            };

            if ('_authLoading' in stepData) {
                const loading = stepData._authLoading ?? false;
                onAuthLoadingChange?.(loading);
                return;
            }

            if ('_authFetchersBusy' in stepData) {
                setAuthEmailPasswordFetchersBusy(
                    stepData._authFetchersBusy ?? false,
                );
                return;
            }

            if ('showEmailForm' in stepData) {
                const emailFormVisible = stepData.showEmailForm ?? false;
                setShowEmailForm(emailFormVisible);
                onAuthViewChange?.(emailFormVisible);
                return;
            }

            if ('showForgotPassword' in stepData) {
                setShowForgotPassword(stepData.showForgotPassword ?? false);
                return;
            }

            const {
                _hdeCompanyMarketingConsent: companyMarketingConsent,
                ...formStepData
            } = stepData;

            if (companyMarketingConsent !== undefined) {
                setHdeCompanyMarketingConsentChecked(
                    Boolean(companyMarketingConsent),
                );
            }

            setFormData((prev) => ({ ...prev, ...formStepData }));
        },
        [onAuthViewChange, onAuthLoadingChange],
    );

    const submitRegistration = useCallback(() => {
        if (hasSubmitted || isSubmitting) return;
        // Defensive: an invited guest must never reach a primary submission.
        if (showEventGuestBlock) return;

        setIsSubmitting(true);
        setHasSubmitted(true);
        const submitData = buildSubmitPayload(
            formData,
            user && (user as { __typename?: string }).__typename === 'Me'
                ? user
                : null,
            professionalExtraction,
        );
        fetcher.submit(submitData, { method: 'post', action: HDE_PAGE_PATH });
    }, [
        formData,
        user,
        fetcher,
        hasSubmitted,
        isSubmitting,
        buildSubmitPayload,
        professionalExtraction,
        showEventGuestBlock,
    ]);

    const submitRegistrationRef = useRef(submitRegistration);
    submitRegistrationRef.current = submitRegistration;

    const handleNext = useCallback(() => {
        const currentStep = visibleSteps[uiFormState.currentStepIndex];

        if (currentStep?.id === 'professional-member') {
            uiFormState.goToNext();
            return;
        }

        if (currentStep?.id === 'authentication') {
            if (isLoggedIn) {
                // Wait for the existing-submission lookup before advancing; the checking
                // view covers the delay after login on this first step.
                if (!isCheckingExistingSubmission && !needsPrefill) {
                    uiFormState.goToNext();
                }
            } else if (!showForgotPassword && !mobile) {
                /** Desktop: modal footer Continue submits `#loginSignUpForm`. Mobile uses in-form buttons. */
                const formEl = document.getElementById('loginSignUpForm');
                if (formEl instanceof HTMLFormElement) {
                    formEl.requestSubmit();
                }
            }
            return;
        }

        if (
            currentStep?.id === 'event-selection' &&
            (isCheckingExistingSubmission || needsPrefill)
        ) {
            return;
        }

        if (
            currentStep?.id === 'business-profile-picker' &&
            isProfessionalExtractionLoading
        ) {
            return;
        }

        if (hasGuestInvitation && currentStep?.id === 'guest-invitation') {
            const fd = formData as {
                bringGuest?: boolean;
                guestFormView?: boolean;
            };
            if (fd.bringGuest === true && !fd.guestFormView) {
                handleStepDataChange({
                    guestFormView: true,
                } as unknown as Partial<TFormData>);
                return;
            }
            uiFormState.goToNext();
            return;
        }

        if (
            currentStep?.id === 'company-name-website' &&
            !hdeCompanyMarketingConsentChecked
        ) {
            return;
        }

        uiFormState.goToNext();
    }, [
        visibleSteps,
        uiFormState,
        isLoggedIn,
        showForgotPassword,
        mobile,
        hasGuestInvitation,
        formData,
        handleStepDataChange,
        isProfessionalExtractionLoading,
        hdeCompanyMarketingConsentChecked,
        isCheckingExistingSubmission,
        needsPrefill,
    ]);

    useEffect(() => {
        if (currentStepId === 'authentication') {
            wasOnAuthStep.current = true;
        }
    }, [currentStepId]);

    useEffect(() => {
        const justLoggedIn = isLoggedIn && !prevIsLoggedIn.current;
        prevIsLoggedIn.current = isLoggedIn;
        if (justLoggedIn && wasOnAuthStep.current) {
            wasOnAuthStep.current = false;
            goToStepRef.current(0);
        }
    }, [isLoggedIn]);

    useEffect(() => {
        const authStepVisible = visibleSteps.some(
            (s) => s.id === 'authentication',
        );
        // Legacy OAuth auto-submit was removed; always submit from the success step.
        const shouldSubmit =
            currentStepId === 'success' &&
            (isLoggedIn || !authStepVisible) &&
            !hasSubmitted &&
            !isSubmitting;

        if (shouldSubmit) {
            hasProcessedResponseRef.current = false;
            submitRegistrationRef.current();

            submissionTimeoutRef.current = setTimeout(() => {
                setIsSubmitting(false);
                goToNextRef.current();
            }, 30000);
        }

        return () => {
            if (submissionTimeoutRef.current) {
                clearTimeout(submissionTimeoutRef.current);
                submissionTimeoutRef.current = null;
            }
        };
    }, [currentStepId, hasSubmitted, isSubmitting, isLoggedIn, visibleSteps]);

    useEffect(() => {
        const isRequestComplete =
            fetcher.state === 'idle' || fetcher.state === 'loading';
        const hasResponseData =
            fetcher.data !== null && fetcher.data !== undefined;
        const isOnPostSubmissionStep = currentStepId === 'success';
        const shouldProcess =
            isRequestComplete &&
            fetcher.formMethod === 'POST' &&
            hasSubmitted &&
            isOnPostSubmissionStep &&
            hasResponseData &&
            !hasProcessedResponseRef.current;

        if (shouldProcess) {
            hasProcessedResponseRef.current = true;

            if (submissionTimeoutRef.current) {
                clearTimeout(submissionTimeoutRef.current);
                submissionTimeoutRef.current = null;
            }

            const response = fetcher.data as
                | { errors?: unknown[]; success?: boolean }
                | null
                | undefined;

            const hasErrors =
                response &&
                typeof response === 'object' &&
                'errors' in response &&
                Array.isArray(response.errors) &&
                response.errors.length > 0;

            const isSuccess =
                response &&
                typeof response === 'object' &&
                response.success === true;

            if (hasErrors) {
                const errs = (response as { errors: unknown[] }).errors;
                const errorMessages = errs
                    .map((err: unknown) => {
                        if (
                            typeof err === 'object' &&
                            err !== null &&
                            'message' in err
                        ) {
                            return String(
                                (err as { message: unknown }).message,
                            );
                        }
                        return String(err);
                    })
                    .filter((msg): msg is string => Boolean(msg));

                const firstErrorMessage = errorMessages[0];
                const errorMessage: string =
                    firstErrorMessage ??
                    'An error occurred while submitting your registration. Please try again.';

                toast(errorMessage, { type: 'error' });

                setIsSubmitting(false);
                setHasSubmitted(false);

                // When validation fails for company type/location, navigate to the
                // relevant step. Keep those steps visible until user fixes and resubmits.
                setHasCompanyValidationError(true);

                const hasProjectLocationError = errs.some((err: unknown) => {
                    if (typeof err === 'object' && err !== null) {
                        const field = (err as { field?: string }).field;
                        if (
                            field === 'ProjectLocation' ||
                            field === 'CompanyLocation'
                        )
                            return true;
                        const msg = String(
                            (err as { message?: unknown }).message ?? '',
                        );
                        return (
                            msg.includes('ProjectLocation') ||
                            msg.includes('CompanyLocation') ||
                            msg.includes('Project location') ||
                            msg.includes('Company location')
                        );
                    }
                    return false;
                });
                const hasCompanyTypeError = errs.some((err: unknown) => {
                    if (typeof err === 'object' && err !== null) {
                        const field = (err as { field?: string }).field;
                        if (field === 'CompanyType') return true;
                        const msg = String(
                            (err as { message?: unknown }).message ?? '',
                        );
                        return (
                            msg.includes('CompanyType') ||
                            msg.includes('Company type') ||
                            msg.includes('company type')
                        );
                    }
                    return false;
                });

                const companyLocationStepIndex = visibleSteps.findIndex(
                    (s) => s.id === 'company-location',
                );
                const companyTypeStepIndex = visibleSteps.findIndex(
                    (s) => s.id === 'company-type',
                );
                if (hasProjectLocationError && companyLocationStepIndex >= 0) {
                    goToStepRef.current(companyLocationStepIndex);
                } else if (hasCompanyTypeError && companyTypeStepIndex >= 0) {
                    goToStepRef.current(companyTypeStepIndex);
                } else {
                    goToPreviousRef.current();
                }
                return;
            }

            if (isSuccess) {
                setIsSubmitting(false);
                setIsSubmitSuccess(true);
                setHasCompanyValidationError(false);
            } else {
                toast(
                    'An error occurred while submitting your registration. Please try again.',
                    { type: 'error' },
                );
                setIsSubmitting(false);
                setHasSubmitted(false);
                goToPreviousRef.current();
            }
        }
    }, [
        fetcher.state,
        fetcher.formMethod,
        fetcher.data,
        hasSubmitted,
        currentStepId,
        toast,
        visibleSteps,
    ]);

    useEffect(() => {
        if (currentStepId) onStepChange?.(currentStepId);
    }, [currentStepId, onStepChange]);

    useEffect(() => {
        if (currentStepId !== 'authentication') {
            onAuthLoadingChange?.(false);
        }
    }, [currentStepId, onAuthLoadingChange]);

    const isAuthStep = currentStepId === 'authentication';
    const isSuccessStep = currentStepId === 'success';
    const isIntroStep = currentStepId === 'intro';
    const isApplicationTypeStep = currentStepId === 'application-type';
    const isEventSelectionStep = currentStepId === 'event-selection';
    const isGuestQuestionView =
        hasGuestInvitation &&
        currentStepId === 'guest-invitation' &&
        !(formData as { guestFormView?: boolean }).guestFormView;
    const isProjectLocationOtherInput =
        currentStepId === 'project-location' &&
        (formData as { showOtherInput?: boolean }).showOtherInput === true;
    const isCompanyLocationOtherInput =
        currentStepId === 'company-location' &&
        (formData as { companyShowOtherInput?: boolean })
            .companyShowOtherInput === true;
    const isCompanyTypeOtherSelected =
        currentStepId === 'company-type' &&
        (formData as { companyType?: string }).companyType === 'Other';
    const isExistingSubmissionStep = currentStepId === 'existing-submission';
    // Keep the checking view up until both the lookup has resolved AND any same-type
    // prefill has been committed to formData. Otherwise step 0 mounts before the
    // prefill arrives and its option buttons never reflect the selected values.
    const showCheckingExistingSubmission =
        (isCheckingExistingSubmission || needsPrefill) && !isEventSelectionStep;
    const isAuthInitialView = isAuthStep && !showEmailForm;
    const hideStepIndicator =
        isApplicationTypeStep ||
        isAuthStep ||
        isSuccessStep ||
        isExistingSubmissionStep;
    const stepIndicatorLabel = isIntroStep
        ? 'YOUR JOURNEY STARTS HERE'
        : undefined;
    const stepIndicatorClassName = isIntroStep ? 'md:hidden' : undefined;
    /**
     * Forgot-password sub-view keeps its own row. On mobile, auth keeps in-form Back/Continue
     * (footer hidden); on desktop, auth uses the shared modal footer.
     */
    const hideFooter =
        isSuccessStep ||
        isExistingSubmissionStep ||
        (isAuthStep && showForgotPassword) ||
        (isAuthStep && mobile);

    useEffect(() => {
        if (currentStepId !== 'authentication') {
            setAuthEmailPasswordFetchersBusy(false);
        }
    }, [currentStepId]);

    const indicatorSteps = useMemo(
        () =>
            visibleSteps.filter(
                (step) => !HIDE_INDICATOR_STEP_IDS.has(step.id),
            ),
        [visibleSteps],
    );
    const useDefaultIndicator =
        (isIntroStep || isApplicationTypeStep) &&
        defaultTotalStepsForIndicator != null;
    const useProfessionalMemberIndicator =
        currentStepId === 'professional-member' &&
        (applicationType === 'Professional' || applicationType === 'Exhibitor');
    /**
     * First-step totals align with `indicatorSteps.length + indicatorStepOffset` on the typical
     * pro/exhibitor path now that `business-profile-picker` counts toward progress (Professional
     * includes guest-invitation; Exhibitor does not).
     */
    const professionalMemberIndicator = useMemo(
        () =>
            applicationType === 'Professional'
                ? { current: 1, total: 7 }
                : applicationType === 'Exhibitor'
                  ? { current: 1, total: 6 }
                  : null,
        [applicationType],
    );
    const currentVisualDotIndex = useMemo(() => {
        if (useProfessionalMemberIndicator && professionalMemberIndicator) {
            return professionalMemberIndicator.current;
        }
        if (useDefaultIndicator) {
            return 0;
        }
        const idx = indicatorSteps.findIndex(
            (step) => step.id === currentStepId,
        );
        const base = idx >= 0 ? idx : uiFormState.currentStepIndex;
        return base + indicatorStepOffset;
    }, [
        useProfessionalMemberIndicator,
        professionalMemberIndicator,
        useDefaultIndicator,
        indicatorSteps,
        currentStepId,
        uiFormState.currentStepIndex,
        indicatorStepOffset,
    ]);
    const totalVisualDots =
        useProfessionalMemberIndicator && professionalMemberIndicator
            ? professionalMemberIndicator.total
            : useDefaultIndicator
              ? defaultTotalStepsForIndicator
              : indicatorSteps.length + indicatorStepOffset;

    const isFirstStep = uiFormState.currentStepIndex === 0;

    const resetGuestForm = useCallback(() => {
        handleStepDataChange({
            bringGuest: null,
            guestFormView: false,
        } as unknown as Partial<TFormData>);
    }, [handleStepDataChange]);

    const resetProjectLocationOtherInput = useCallback(() => {
        handleStepDataChange({
            showOtherInput: false,
            projectLocation: '',
            selectedLocation: null,
        } as unknown as Partial<TFormData>);
    }, [handleStepDataChange]);

    const resetCompanyLocationOtherInput = useCallback(() => {
        handleStepDataChange({
            companyShowOtherInput: false,
            companyLocation: '',
            companySelectedLocation: null,
        } as unknown as Partial<TFormData>);
    }, [handleStepDataChange]);

    const handleBack = useCallback(() => {
        if (
            currentStepId === 'project-location' &&
            (formData as { showOtherInput?: boolean }).showOtherInput === true
        ) {
            resetProjectLocationOtherInput();
        } else if (
            currentStepId === 'company-location' &&
            (formData as { companyShowOtherInput?: boolean })
                .companyShowOtherInput === true
        ) {
            resetCompanyLocationOtherInput();
        } else if (
            hasGuestInvitation &&
            currentStepId === 'guest-invitation' &&
            (formData as { bringGuest?: boolean }).bringGuest === true
        ) {
            resetGuestForm();
        } else if (isFirstStep) {
            onBackToApplicationType?.();
            return;
        } else {
            uiFormState.goToPrevious();
        }
    }, [
        hasGuestInvitation,
        currentStepId,
        formData,
        isFirstStep,
        onBackToApplicationType,
        resetGuestForm,
        resetProjectLocationOtherInput,
        resetCompanyLocationOtherInput,
        uiFormState,
    ]);

    const handleCancelOrBack = useCallback(() => {
        if (
            hasGuestInvitation &&
            currentStepId === 'guest-invitation' &&
            (formData as { bringGuest?: boolean }).bringGuest === true
        ) {
            resetGuestForm();
        } else {
            onClose?.();
        }
    }, [hasGuestInvitation, currentStepId, formData, resetGuestForm, onClose]);

    const renderForm = useCallback(() => {
        if (showCheckingExistingSubmission) {
            return (
                <div
                    className={cn(
                        'relative flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden bg-[#EAEAEA]',
                        layout === 'split' && 'max-w-[100vw] rounded-r-2xl',
                    )}
                >
                    <div className="flex flex-col items-center gap-3 text-[#2A2A2A]/70">
                        <span className="size-6 animate-spin rounded-full border-2 border-[#2A2A2A]/20 border-t-[#2A2A2A]/70" />
                        <span className="text-[0.85rem]">
                            Checking your registration…
                        </span>
                    </div>
                </div>
            );
        }

        if (showEventGuestBlock) {
            return (
                <div
                    className={cn(
                        'relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[#EAEAEA] px-8',
                        layout === 'split' && 'max-w-[100vw] rounded-r-2xl',
                    )}
                >
                    <div className="flex flex-1 flex-col items-center justify-center">
                        <p className="max-w-[26rem] text-center text-[0.95rem] leading-relaxed text-[#2A2A2A]">
                            {HDE_EVENT_GUEST_BLOCK_MESSAGE}
                        </p>
                    </div>
                    <div className="flex flex-col items-center justify-center pb-20">
                        <Button
                            onClick={() => onClose?.()}
                            color="gray"
                            className="h-[2.8125rem] min-w-[11.125rem] rounded-full bg-[#38383B] font-medium text-white md:hover:bg-[#38383B]/90"
                        >
                            Close
                        </Button>
                    </div>
                </div>
            );
        }

        const formContent = (
            <MultiStepFormContainer
                stepConfigs={visibleSteps}
                currentStepIndex={safeStepIndex}
                totalSteps={visibleSteps.length}
                currentVisualDotIndex={currentVisualDotIndex}
                totalVisualDots={totalVisualDots}
                isStepValid={uiFormState.isStepValid}
                isLoading={
                    isSubmitting ||
                    (currentStepId === 'business-profile-picker' &&
                        isProfessionalExtractionLoading) ||
                    (isEventSelectionStep &&
                        (isCheckingExistingSubmission || needsPrefill)) ||
                    (isAuthStep && authEmailPasswordFetchersBusy && !mobile)
                }
                errors={{}}
                nextButtonText={
                    isIntroStep
                        ? "Let's get started"
                        : isAuthStep && !showForgotPassword && !mobile
                          ? 'Continue'
                          : undefined
                }
                nextButtonClassName={
                    isIntroStep
                        ? 'min-w-[12.5rem] !transition-none [--rocco-btn-bg:#38383B] [--rocco-btn-border:transparent] [--rocco-btn-text:#ffffff] [--rocco-btn-fill:#ffffff]'
                        : undefined
                }
                hideNextButton={
                    isProjectLocationOtherInput ||
                    isCompanyLocationOtherInput ||
                    isCompanyTypeOtherSelected
                        ? false
                        : isGuestQuestionView || undefined
                }
                hideCancelButton={isIntroStep}
                initialData={
                    currentStepId
                        ? getInitialDataForStep(currentStepId)
                        : undefined
                }
                onNext={handleNext}
                onBack={handleBack}
                onCancel={handleCancelOrBack}
                onSkip={uiFormState.goToNext}
                onStepDataChange={handleStepDataChange}
                setIsValid={uiFormState.setStepValid}
                variant="hde"
                className={cn(
                    layout === 'split' && '!bg-[#EAEAEA]',
                    layout !== 'split' && isAuthInitialView && 'bg-transparent',
                    layout !== 'split' && !isAuthInitialView && '!bg-[#EAEAEA]',
                )}
                hideStepIndicator={hideStepIndicator}
                stepIndicatorLabel={stepIndicatorLabel}
                stepIndicatorClassName={stepIndicatorClassName}
                hideFooter={hideFooter}
            />
        );

        if (layout === 'split') {
            return (
                <div className="relative flex min-h-0 max-w-[100vw] flex-1 flex-col overflow-hidden rounded-r-2xl bg-[#EAEAEA]">
                    {formContent}
                </div>
            );
        }

        return (
            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
                {isAuthInitialView && !isFirstStep && (
                    <div className="flex h-14 shrink-0 items-center px-4 md:px-6">
                        <button
                            type="button"
                            onClick={handleBack}
                            className="flex items-center text-white transition-colors md:hover:text-white/80"
                            aria-label="Go to previous step"
                        >
                            <ArrowLeft className="size-5 fill-white" />
                        </button>
                    </div>
                )}
                {formContent}
            </div>
        );
    }, [
        visibleSteps,
        safeStepIndex,
        currentVisualDotIndex,
        totalVisualDots,
        uiFormState.isStepValid,
        uiFormState.goToNext,
        uiFormState.setStepValid,
        isSubmitting,
        isProfessionalExtractionLoading,
        currentStepId,
        getInitialDataForStep,
        handleNext,
        handleBack,
        handleCancelOrBack,
        handleStepDataChange,
        isAuthInitialView,
        isFirstStep,
        isGuestQuestionView,
        isProjectLocationOtherInput,
        isCompanyLocationOtherInput,
        isCompanyTypeOtherSelected,
        isEventSelectionStep,
        hideStepIndicator,
        stepIndicatorLabel,
        stepIndicatorClassName,
        hideFooter,
        isIntroStep,
        isAuthStep,
        showForgotPassword,
        authEmailPasswordFetchersBusy,
        mobile,
        layout,
        showCheckingExistingSubmission,
        isCheckingExistingSubmission,
        needsPrefill,
        showEventGuestBlock,
        onClose,
    ]);

    return {
        formData,
        setFormData,
        visibleSteps,
        safeStepIndex,
        currentStepId,
        isSubmitting,
        uiFormState,
        handleStepDataChange,
        handleNext,
        handleBack,
        handleCancelOrBack,
        getInitialDataForStep,
        isAuthInitialView,
        hideStepIndicator,
        hideFooter,
        indicatorSteps,
        currentVisualDotIndex,
        renderForm,
    };
}
